import {useLocation, useNavigate, useParams} from "react-router-dom";
import api from "../../../services/api";
import {useEffect, useRef, useState} from "react";
import '../../../assets/css/search.css';
import {useRecoilState, useRecoilValue} from "recoil";
import {selectedPerson} from "../../../recoils/recoil";

/**
 * 전역 인물 검색 > 인물 선택
 *
 */
const PersonChoice = () => {

    const {personName} = useParams();

    const [searchName, setSearchName] = useState(null);
    const navigate = useNavigate();

    // 전체 데이터 배열
    const [allPersonList, setAllPersonList] = useState([]);
    // 현재 화면에 보여줄 데이터
    const [displayedPersonList, setDisplayedPersonList] = useState([]);
    const [chosenPerson, setChosenPerson] = useRecoilState(selectedPerson);

    const term = useRef('');
    const location = useLocation();

    const [isLoading, setIsLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);

    // 모바일 툴팁 관련
    const [isTooltipVisible, setTooltipVisible] = useState(false);
    const toolTipRef = useRef(null);

    const selectedPersonList = useRecoilValue(selectedPerson);

    // 리코일에 저장한 상태값을 미리 넣어놓기
    const [selectedPersonIds, setSelectedPersonIds] = useState(
        selectedPersonList.map(person => person.id)
    );

    // 그루핑된 검색 결과
    const [groupedPersonList, setGroupedPersonList] = useState({});

    // 페이지네이션 관련 상태
    const [page, setPage] = useState(1);
    const limit = 50; // 한 번에 100개씩 표시
    const observerRef = useRef(null);

    // 최초 검색 또는 페이지 진입 시 모든 데이터 로드
    useEffect(() => {
        const isNavigatingBack = location.state ? location.state.isPersonBack : null;

        // 상단 nav에서의 검색 로직
        const getPerson = async () => {
            await setSearchName(personName);
            try {
                setIsLoading(true);
                // 모든 데이터 로드
                const response = await api.post('/get_persons_by_person_name', {
                    name_text: personName
                });

                if (response.status === 200) {
                    const filteredData = response.data.data.map((person) => ({
                        person_id: person.person_id,
                        primaryname: person.primaryname,
                        primaryname_ko: person.primaryname_ko,
                        name_list: person.name_list,
                        n_books: person.n_books,
                        n_events: person.n_events,
                        n_institutes: person.n_institutes,
                        n_orders: person.n_orders,
                        n_regions: person.n_regions,
                        n_schools: person.n_schools,
                        n_tablerecords: person.n_tablerecords,
                        n_tocrecords: person.n_tocrecords,
                        n_tocs: person.n_tocs,
                        n_tables: person.n_tables,
                    }));

                    const sortedData = filteredData.sort((a, b) => {
                        if (a.primaryname_ko < b.primaryname_ko) return -1;
                        if (a.primaryname_ko > b.primaryname_ko) return 1;
                        return 0;
                    });

                    setAllPersonList(sortedData);
                    setTotalCount(sortedData.length);

                    // 첫 100개 표시
                    setDisplayedPersonList(sortedData.slice(0, limit));
                }
            } catch (error) {
                console.error(error);
            } finally {
                setIsLoading(false);
            }
        };

        // isNavigatingBack이 true가 아닐 경우에만 getPerson 실행
        if (isNavigatingBack !== true) {
            getPerson();
        }

        // // 모바일 툴팁 외부 영역 클릭 시 툴팁 닫기
        document.addEventListener("click", handleClickOutside);
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [personName, location.state]);

    // 무한 스크롤 Intersection Observer 설정
    useEffect(() => {

        const currentObserverRef = observerRef.current; // Store ref value

        // 추가 데이터 로드
        const loadMoreData = () => {

            const nextPage = page + 1;
            const startIndex = page * limit;
            const endIndex = nextPage * limit;

            const moreData = allPersonList.slice(startIndex, endIndex);
            setDisplayedPersonList(prev => [...prev, ...moreData]);
            setPage(nextPage);


        };

        // Observer의 콜백함수 선언
        const onIntersection = (entries) => {
            const entry = entries[0];  // 첫 번째 entry만 확인
            if (entry.isIntersecting && displayedPersonList.length < allPersonList.length) {
                loadMoreData();
            }
        };

        const observer = new IntersectionObserver(onIntersection, {
            root: null,
            rootMargin: '100px',  // 뷰포트 아래 100px 지점에서 미리 로드 시작
            threshold: 0.1  // 10%만 보여도 로드 시작
        });

        if (currentObserverRef) {
            observer.observe(currentObserverRef);
        }

        return () => {
            if (currentObserverRef) {
                observer.unobserve(currentObserverRef);
            }
            observer.disconnect();
        };
    }, [allPersonList, page, limit]);

    // displayedPersonList가 변경될 때마다 그룹화된 결과 업데이트
    useEffect(() => {
        setGroupedPersonList(groupPersonsByPrimaryName(displayedPersonList));
    }, [displayedPersonList]);


    const handleClickOutside = (e) => {
        if (toolTipRef.current && !toolTipRef.current.contains(e.target)) {
            setTooltipVisible(false);
        }
    };

    // 인물 선택하기
    // personId로 체크박스 on, off 결정
    const selectPerson = (personId, personName, personNameKo) => {
        // 선택된 사람 id에 personId 추가 혹은 제거
        setSelectedPersonIds(prevIds =>
            prevIds.includes(personId) ? prevIds.filter(id => id !== personId) : [...prevIds, personId]
        );

        setChosenPerson(prevData => {
            const isPersonSelected = prevData.some(person => person.id === personId);

            if (isPersonSelected) {
                return prevData.filter(person => person.id !== personId);
            } else {
                return [...prevData, {
                    id: personId,
                    name: personName,
                    nameKo: personNameKo,
                }];
            }
        });
    }

    // main 부분에서의 검색 시 작동
    const getOtherPerson = async () => {
        await setSearchName(term.current);

        try {
            setIsLoading(true);
            const response = await api.post('/get_persons_by_person_name', {
                name_text: term.current
            });

            if (response.status === 200) {
                const filteredData = response.data.data.map((person) => ({
                    person_id: person.person_id,
                    primaryname: person.primaryname,
                    primaryname_ko: person.primaryname_ko,
                    name_list: person.name_list,
                    n_books: person.n_books,
                    n_events: person.n_events,
                    n_institutes: person.n_institutes,
                    n_orders: person.n_orders,
                    n_regions: person.n_regions,
                    n_schools: person.n_schools,
                    n_tablerecords: person.n_tablerecords,
                    n_tocrecords: person.n_tocrecords,
                    n_tocs: person.n_tocs,
                    n_tables: person.n_tables,
                }));

                setAllPersonList(filteredData);
                setTotalCount(filteredData.length);
                setDisplayedPersonList(filteredData.slice(0, limit));
                setPage(1);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    // 인물 idList 변수 생성 - useNavigate로 보내기
    const searchPerson = () => {
        let idList = [];

        chosenPerson.forEach(person => {
            idList.push(person.id);
        })
        navigate(`/person/${idList}`);
    }

    // 삭제 함수
    const handleDeletePerson = (personId) => {
        setSelectedPersonIds(prevIds => prevIds.filter(id => id !== personId));
        setChosenPerson(prevData => prevData.filter(person => person.id !== personId));
    }

    // 초기화 함수
    const handleClearPerson = () => {
        setSelectedPersonIds([])
        setChosenPerson([])
    }

    /**
     * 검색창 관련 함수들
     */

        // 검색어 지정
    const handleTerm = (e) => {
            term.current = e.target.value;
        }

    // 검색
    const handleSearch = () => {
        if (term.current.length > 0) {
            getOtherPerson();
        }
    }


    const onKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault(); // 폼 제출 방지. 폼 제출로 인해 페이지 새로고침됨
            getOtherPerson();
        }
    };

    /**
     * 그루핑 관련 함수들
     */
        // 검색 결과를 primaryname으로 그룹화하는 함수
    const groupPersonsByPrimaryName = (persons) => {

            // reduce() 메서드를 사용해 acc (누적 객체)를 초기값 {}으로 설정
            // 배열의 모든 요소에 함수 적용
            // array.reduce((accumulator, currentValue) => { ... }, initialValue)
            return persons.reduce((acc, person) => {
                if (!acc[person.primaryname]) {
                    acc[person.primaryname] = [];
                }

                // person.primaryname을 키로 해서 푸시
                acc[person.primaryname].push(person);
                return acc;
            }, {});
        };

    // 그룹 전체를 선택/해제하는 함수
    const selectGroup = (primaryname) => {
        const group = groupedPersonList[primaryname];
        //  배열의 모든 요소가 제공된 함수로 구현된 테스트를 통과하는지(여기선 모두 선택이 되어 있는지 여부)
        const allSelected = group.every(person => selectedPersonIds.includes(person.person_id));

        if (allSelected) {
            // 모두 선택되어 있다면 전체 해제
            setSelectedPersonIds(prevIds => prevIds.filter(id => !group.some(person => person.person_id === id)));
            setChosenPerson(prevData => prevData.filter(person => !group.some(groupPerson => groupPerson.person_id === person.id)));
        } else {
            // 일부만 선택되어 있거나 전체 미선택이면 전체 선택
            const newIds = group.map(person => person.person_id);
            setSelectedPersonIds(prevIds => [...new Set([...prevIds, ...newIds])]);
            setChosenPerson(prevData => {


                const newPersons = group.map(person => ({
                    id: person.person_id,
                    name: person.primaryname,
                    nameKo: person.primaryname_ko,
                }));

                return [...prevData.filter(p => !newIds.includes(p.id)), ...newPersons];
            });
        }
    };

    // 검색창 필요 검색 결과를 페이지 이동하지 말고 아래에 뿌리기
    return (
        <main id="contents">
            <div className="sub-content">
                <div className="search-wrap">
                    <div className="cont-top">
                        <div className="w-set">
                            <div className="title-wrap">
                                <h2 className="title batang">주체</h2>
                                <p className="text">일제강점기 학력 엘리트 및 교원정보를 <br/>찾아볼 수 있습니다.</p>
                            </div>
                        </div>
                    </div>
                    <div className="cont-btm">
                        <div className="w-set">
                            <div className="search-list-wrap">
                                <div className="search-top">
                                    <div className="text-wrap">
                                        <p>인물</p>
                                        <div className="tooltip-wrap" ref={toolTipRef}>
                                            <button type="button" className="i-tool"
                                                onClick={() => setTooltipVisible(!isTooltipVisible)}
                                            ></button>
                                            <div className={`tool-box ${isTooltipVisible ? 'on' : ''}`}>원본의 이름 오기, 한자 폰트 등의 이유로 한 인물이 여러 이름을 가질 수 있습니다. <br/>여러 이름으로 검색하여 자료를
                                            모아보세요. 새로운 이름으로 검색해도 선택한 인물은 유지됩니다.</div>
                                        </div>
                                    </div>
                                    <div className="search-wrap">
                                        <form>
                                            <div className="search-box">
                                                <div className="search-area">
                                                    <div className="input-wrap">
                                                        <label htmlFor="search">
                                                            <input
                                                                type="text"
                                                                name="search"
                                                                placeholder="인물명을 입력해주세요."
                                                                onChange={handleTerm}
                                                                onKeyDown={onKeyPress}
                                                            />
                                                        </label>
                                                        <button type="button" onClick={handleSearch}
                                                                className="btn-search">검색
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                                <div className="search-btm">
                                    <form>
                                        <div className="filter-wrap">
                                            <div className="filter-left">
                                                <div className="total-wrap">
                                                    <p>선택한 인물 총 <span>{chosenPerson.length}</span></p>
                                                    <button type="reset" className="btn-reset"
                                                            onClick={() => handleClearPerson()}>초기화
                                                    </button>
                                                </div>
                                                <div className="name-wrap">
                                                    <ul className="name-list">
                                                        {chosenPerson.map((person, index) => (
                                                            <li key={person.id}>
                                                                <span className="num">{index + 1}</span>
                                                                <p className="name">{person.nameKo} {person.name}</p>
                                                                <button
                                                                    type="button"
                                                                    className="btn-delete"
                                                                    onClick={() => handleDeletePerson(person.id)}
                                                                >
                                                                    삭제
                                                                </button>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                    <div className="btn-wrap">
                                                        <button className="btn" onClick={searchPerson}><i
                                                            className="i-search"></i>자료검색
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="filter-right">
                                                <div className="legend-wrap legend-indicate">
                                                    <ul className="legend-area">
                                                        <li><i className="books"></i>책</li>
                                                        <li><i className="tocs"></i>목차</li>
                                                        <li><i className="tocrecords"></i>책내용</li>
                                                        <li><i className="tables"></i>표</li>
                                                        <li><i className="tablerecords"></i>표내용</li>
                                                        <li><i className="orders"></i>직원록/인사명령</li>
                                                        <li><i className="events"></i>회고록 내용</li>
                                                        <li><i className="institutes"></i>기관</li>
                                                        <li><i className="regions"></i>지리</li>
                                                        <li><i className="schools"></i>학교</li>
                                                    </ul>
                                                </div>

                                                {/* 25.02.10 첫 진입 시 보이는 화면 */}
                                                <div className="before-searching"
                                                     style={{display: isLoading === false && searchName === null ? 'block' : 'none'}}>
                                                    <div className="empty person">
                                                        <p>인물명을 입력하시면 검색결과가 나옵니다.</p>
                                                    </div>
                                                </div>

                                                <div className="result-wrap">
                                                    <p className="result-text"
                                                       style={{display: isLoading === false && searchName !== null ? 'block' : 'none'}}>
                                                        {/*검색어는 span 태그 안에 추가*/}
                                                        <span>&#8220;{searchName}&#8220;</span>
                                                        로 검색하신 결과는 <em>총 {totalCount}건</em> 입니다.
                                                    </p>

                                                    {Object.entries(groupedPersonList).map(([primaryname, group]) => (
                                                        <div className="section-wrap" key={primaryname}>
                                                            {/*객체에서 2차원 배열(이름과 같은 이름을 가진 사람)로 변환하고 map 돌리기*/}
                                                            <div>
                                                                {/* 전체 선택 */}
                                                                <div className="section-title">
                                                                    <div className="checkbox">
                                                                        <label>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={group.every(person => selectedPersonIds.includes(person.person_id))}
                                                                                onChange={() => selectGroup(primaryname)}
                                                                            />
                                                                            <div className="label">
                                                                                <p className="name">
                                                                                    <span
                                                                                        className="bold">{primaryname}</span>
                                                                                </p>
                                                                            </div>
                                                                        </label>
                                                                    </div>
                                                                </div>

                                                                <div className="table-wrap"
                                                                     style={{display: isLoading === false ? 'block' : 'none'}}>
                                                                    {group.map((person) => (
                                                                        <div className="list-row"
                                                                             key={person.person_id}>
                                                                            <div className="checkbox">
                                                                                <label
                                                                                    htmlFor={`person_${person.person_id}`}>
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        name="person"
                                                                                        id={`person_${person.person_id}`}
                                                                                        checked={selectedPersonIds.includes(person.person_id)}
                                                                                        onChange={() => selectPerson(person.person_id, person.primaryname, person.primaryname_ko)}
                                                                                    />
                                                                                    <div className="label">
                                                                                        <p className="name">
                                                                                            <span
                                                                                                className="bold">{person.primaryname}</span>
                                                                                        </p>
                                                                                    </div>
                                                                                </label>
                                                                            </div>

                                                                            {/* 중복 필터링 후 렌더링 */}
                                                                            <div className="sub-name">
                                                                                {person.name_list.filter(name => name !== person.primaryname) // primaryname_ko 만 제거된 배열 생성
                                                                                    // 필터링된 배열에 map 수행
                                                                                    .map(name => (
                                                                                        <span key={name}>{name}</span>
                                                                                    ))}
                                                                            </div>
                                                                            <div className="legend-wrap">
                                                                                <ul className="legend-area">
                                                                                    <li style={{display: person.n_tocrecords !== 0 ? "flex" : "none"}}>
                                                                                        <i className="books"></i>{person.n_tocrecords !== 0 ? person.n_tocrecords : ""}
                                                                                    </li>
                                                                                    <li style={{display: person.n_tocrecords !== 0 ? "flex" : "none"}}>
                                                                                        <i className="tocs"></i>{person.n_tocrecords !== 0 ? person.n_tocrecords : ""}
                                                                                    </li>
                                                                                    <li style={{display: person.n_tocrecords !== 0 ? "flex" : "none"}}>
                                                                                        <i className="tocrecords"></i>{person.n_tocrecords !== 0 ? person.n_tocrecords : ""}
                                                                                    </li>

                                                                                    <li style={{display: person.n_tables !== 0 ? "flex" : "none"}}>
                                                                                        <i className="tables"></i>{person.n_tables !== 0 ? person.n_tables : ""}
                                                                                    </li>
                                                                                    <li style={{display: person.n_tablerecords !== 0 ? "flex" : "none"}}>
                                                                                        <i className="tablerecords"></i>{person.n_tablerecords !== 0 ? person.n_tablerecords : ""}
                                                                                    </li>
                                                                                    <li style={{display: person.n_orders !== 0 ? "flex" : "none"}}>
                                                                                        <i className="orders"></i>{person.n_orders !== 0 ? person.n_orders : ""}
                                                                                    </li>

                                                                                    <li style={{display: person.n_events !== 0 ? "flex" : "none"}}>
                                                                                        <i className="events"></i>{person.n_events !== 0 ? person.n_events : ""}
                                                                                    </li>
                                                                                    <li style={{display: person.n_institutes !== 0 ? "flex" : "none"}}>
                                                                                        <i className="institutes"></i>{person.n_institutes !== 0 ? person.n_institutes : ""}
                                                                                    </li>
                                                                                    <li style={{display: person.n_regions !== 0 ? "flex" : "none"}}>
                                                                                        <i className="regions"></i>{person.n_regions !== 0 ? person.n_regions : ""}
                                                                                    </li>
                                                                                    <li style={{display: person.n_schools !== 0 ? "flex" : "none"}}>
                                                                                        <i className="schools"></i>{person.n_schools !== 0 ? person.n_schools : ""}
                                                                                    </li>
                                                                                </ul>
                                                                            </div>
                                                                        </div>
                                                                    ))}

                                                                </div>
                                                            </div>

                                                        </div>
                                                    ))}
                                                            {/* 무한 스크롤을 위한 참조 요소 추가 */}
                                                            <div ref={observerRef} style={{height: '10px'}}></div>
                                                </div>
                                                {/*안보일 때 : display: none / 로딩중일 때 display: block */}
                                                <div className="load-wrap"
                                                     style={{display: isLoading === true ? 'block' : 'none'}}>
                                                    <div className="load-box">
                                                        <div className="square"></div>
                                                        <div className="square"></div>
                                                        <div className="square"></div>
                                                        <div className="square"></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
}
export default PersonChoice;